import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Save, AlertCircle } from "lucide-react";
import { staffApi, type CreateStaffDto } from "@/lib/api/staff";
import { branchesApi } from "@/lib/api/branches";
import { rolesApi } from "@/lib/api/roles";
import { staffCustomFieldsApi, type StaffCustomField } from "@/lib/api/staff-custom-fields";
import { CustomFieldRenderer } from "@/components/forms/CustomFieldRenderer";
import { ReAuthModal } from "@/components/auth/ReAuthModal";
import { useTenant } from "@/contexts/TenantContext";
import { useDepartments } from "@/hooks/useDepartments";
import { useJobTitles } from "@/hooks/useJobTitles";
import { cn } from "@/lib/utils";

const StaffFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const toast = useToast();
  const { isTenantAdmin } = useAccessControl();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [showReAuthModal, setShowReAuthModal] = useState(false);
  const [reAuthPassword, setReAuthPassword] = useState("");

  const [formData, setFormData] = useState<CreateStaffDto>({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    staffId: "",
    roleId: "",
    branchId: "",
    departmentId: "",
    jobTitleId: "",
    customFields: {},
  });

  const [branches, setBranches] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [customFieldsConfig, setCustomFieldsConfig] = useState<StaffCustomField[]>([]);

  const isEditing = !!id;

  const { data: departmentsData = [] } = useDepartments(true, { enabled: isTenantAdmin });
  const { data: jobTitlesData = [] } = useJobTitles(true, { enabled: isTenantAdmin });
  const departments = departmentsData
    .filter((d) => d.isActive)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  const jobTitles = jobTitlesData
    .filter((j) => j.isActive)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

  // Get tenant ID from user membership as fallback if tenant context doesn't have it
  const tenantId = tenant?.id || user?.memberships?.[0]?.tenantId;

  useEffect(() => {
    if (isTenantAdmin) {
      loadBranchesAndRoles();
      loadCustomFieldsConfig();
      if (isEditing && id) {
        loadStaff(id);
      }
    }
  }, [isTenantAdmin, isEditing, id, tenantId]);

  const loadBranchesAndRoles = async () => {
    try {
      const [brs, rls] = await Promise.all([branchesApi.getAll(true), rolesApi.getAll()]);
      const activeBranches = brs
        .filter((b) => b.status === "ACTIVE")
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
      const sortedRoles = [...rls].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      );
      setBranches(activeBranches);
      setRoles(sortedRoles);
    } catch (error) {
      console.error("Failed to load options:", error);
    }
  };

  const loadCustomFieldsConfig = async () => {
    const currentTenantId = tenantId;
    if (!currentTenantId) {
      console.warn("Cannot load custom fields: tenant ID not available");
      return;
    }
    try {
      const config = await staffCustomFieldsApi.getConfig(currentTenantId);
      setCustomFieldsConfig(config.customFields || []);
    } catch (error) {
      console.error("Failed to load custom fields config:", error);
      // Don't show error toast - custom fields are optional
    }
  };

  const loadStaff = async (staffId: string) => {
    try {
      setLoading(true);
      const staffMember = await staffApi.getById(staffId);
      const customFields = (staffMember.profileMetadata as any)?.customFields || {};
      setFormData({
        email: staffMember.email,
        firstName: staffMember.firstName,
        lastName: staffMember.lastName,
        phone: staffMember.phone || "",
        staffId: staffMember.staffId || "",
        roleId: staffMember.roleId || "",
        branchId: staffMember.branchId || "",
        departmentId: staffMember.departmentId,
        jobTitleId: staffMember.jobTitleId,
        customFields,
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to load staff member.");
      navigate("/setup/staff");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email || !formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.firstName || !formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName || !formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.roleId) {
      newErrors.roleId = "Role is required";
    }

    if (!formData.departmentId) {
      newErrors.departmentId = "Department is required";
    }

    if (!formData.jobTitleId) {
      newErrors.jobTitleId = "Job title is required";
    }

    // Validate custom fields
    customFieldsConfig.forEach((field) => {
      if (field.required) {
        const value = formData.customFields?.[field.name];
        if (value === undefined || value === null || value === "") {
          newErrors[`customFields.${field.name}`] = `${field.label} is required`;
        }
      }

      // Validate field-specific rules
      if (formData.customFields?.[field.name] !== undefined) {
        const value = formData.customFields[field.name];
        if (field.validation) {
          if (field.type === "text" || field.type === "textarea") {
            const strValue = String(value);
            if (field.validation.minLength && strValue.length < field.validation.minLength) {
              newErrors[`customFields.${field.name}`] =
                field.validation.customMessage ||
                `Minimum length is ${field.validation.minLength} characters`;
            }
            if (field.validation.maxLength && strValue.length > field.validation.maxLength) {
              newErrors[`customFields.${field.name}`] =
                field.validation.customMessage ||
                `Maximum length is ${field.validation.maxLength} characters`;
            }
          }
          if (field.type === "number") {
            const numValue = Number(value);
            if (field.validation.min !== undefined && numValue < field.validation.min) {
              newErrors[`customFields.${field.name}`] =
                field.validation.customMessage || `Minimum value is ${field.validation.min}`;
            }
            if (field.validation.max !== undefined && numValue > field.validation.max) {
              newErrors[`customFields.${field.name}`] =
                field.validation.customMessage || `Maximum value is ${field.validation.max}`;
            }
          }
        }
      }
    });

    setErrors(newErrors);
    setTouched(new Set(Object.keys(newErrors)));
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => new Set(prev).add(field));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    // Show re-auth modal for sensitive operations
    setShowReAuthModal(true);
  };

  const handleReAuthConfirm = async () => {
    if (!reAuthPassword) {
      toast.error("Please enter your password.");
      return;
    }

    try {
      setSaving(true);

      // Normalize empty strings to undefined for optional fields
      const normalizedData = {
        ...formData,
        branchId:
          formData.branchId && formData.branchId.trim() !== "" ? formData.branchId : undefined,
        phone: formData.phone && formData.phone.trim() !== "" ? formData.phone : undefined,
        staffId: formData.staffId && formData.staffId.trim() !== "" ? formData.staffId : undefined,
      };

      if (isEditing && id) {
        const { email: _email, ...updateData } = normalizedData;
        await staffApi.update(id, updateData, reAuthPassword);
        toast.success(
          `"${formData.firstName} ${formData.lastName}" has been updated successfully.`
        );
      } else {
        await staffApi.create(normalizedData, reAuthPassword);
        toast.success(`Invitation sent to ${formData.email}.`);
      }

      setShowReAuthModal(false);
      setReAuthPassword("");
      navigate("/setup/staff");
    } catch (error: any) {
      if (error.response?.status === 401) {
        toast.error("Invalid password. Please try again.");
        setReAuthPassword("");
      } else {
        toast.error(error.response?.data?.message || "Unable to save. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">You need administrator permissions to manage staff.</p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (loading && isEditing) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <LoadingSpinner size="xl" label="Loading staff member..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer>
        <PageHeader
          title={isEditing ? "Edit Staff Member" : "Add Staff Member"}
          description={
            isEditing
              ? "Update staff member information"
              : "Invite a new staff member to join your library"
          }
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Staff", href: "/setup/staff" },
            { label: isEditing ? "Edit" : "New" },
          ]}
          showSearch={false}
        />

        <form onSubmit={handleSubmit}>
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Personal Information</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="firstName" className="flex items-center gap-1 mb-2">
                  First Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  onBlur={() => handleFieldBlur("firstName")}
                  className={cn(
                    touched.has("firstName") &&
                      errors.firstName &&
                      "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("firstName") && errors.firstName && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="lastName" className="flex items-center gap-1 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  onBlur={() => handleFieldBlur("lastName")}
                  className={cn(
                    touched.has("lastName") &&
                      errors.lastName &&
                      "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("lastName") && errors.lastName && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.lastName}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="email" className="flex items-center gap-1 mb-2">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  onBlur={() => handleFieldBlur("email")}
                  disabled={isEditing}
                  className={cn(
                    touched.has("email") && errors.email && "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("email") && errors.email && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.email}
                  </p>
                )}
                {!isEditing && (
                  <p className="mt-1.5 text-xs text-gray-500">
                    An invitation email will be sent to this address
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="phone" className="mb-2">
                  Phone
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>

              <div>
                <Label htmlFor="staffId" className="mb-2">
                  Staff ID
                </Label>
                <Input
                  id="staffId"
                  value={formData.staffId}
                  onChange={(e) => setFormData({ ...formData, staffId: e.target.value })}
                  placeholder="Optional staff ID"
                />
              </div>
            </div>
          </Card>

          <Card className="p-6 mb-6 bg-white shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Assignment</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="departmentId" className="flex items-center gap-1 mb-2">
                  Department <span className="text-red-500">*</span>
                </Label>
                <select
                  id="departmentId"
                  value={formData.departmentId}
                  onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                  onBlur={() => handleFieldBlur("departmentId")}
                  className={cn(
                    "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary",
                    touched.has("departmentId") &&
                      errors.departmentId &&
                      "border-red-500 focus:border-red-500"
                  )}
                >
                  <option value="">Select department</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                {touched.has("departmentId") && errors.departmentId && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.departmentId}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="jobTitleId" className="flex items-center gap-1 mb-2">
                  Job Title <span className="text-red-500">*</span>
                </Label>
                <select
                  id="jobTitleId"
                  value={formData.jobTitleId}
                  onChange={(e) => setFormData({ ...formData, jobTitleId: e.target.value })}
                  onBlur={() => handleFieldBlur("jobTitleId")}
                  className={cn(
                    "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary",
                    touched.has("jobTitleId") &&
                      errors.jobTitleId &&
                      "border-red-500 focus:border-red-500"
                  )}
                >
                  <option value="">Select job title</option>
                  {jobTitles.map((title) => (
                    <option key={title.id} value={title.id}>
                      {title.name}
                    </option>
                  ))}
                </select>
                {touched.has("jobTitleId") && errors.jobTitleId && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.jobTitleId}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="roleId" className="flex items-center gap-1 mb-2">
                  Role <span className="text-red-500">*</span>
                </Label>
                <select
                  id="roleId"
                  value={formData.roleId}
                  onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
                  onBlur={() => handleFieldBlur("roleId")}
                  className={cn(
                    "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary",
                    touched.has("roleId") && errors.roleId && "border-red-500 focus:border-red-500"
                  )}
                >
                  <option value="">Select role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {touched.has("roleId") && errors.roleId && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.roleId}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="branchId" className="mb-2">
                  Branch
                </Label>
                <select
                  id="branchId"
                  value={formData.branchId || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, branchId: e.target.value || undefined })
                  }
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">No branch assigned</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name} {branch.isMain && "(Main)"}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </Card>

          {/* Custom Fields Section */}
          {customFieldsConfig.length > 0 && (
            <Card className="p-6 mb-6 bg-white shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Additional Information</h3>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {customFieldsConfig.map((field) => (
                  <CustomFieldRenderer
                    key={field.id}
                    field={field}
                    value={formData.customFields?.[field.name]}
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        customFields: {
                          ...formData.customFields,
                          [field.name]: value,
                        },
                      });
                    }}
                    error={errors[`customFields.${field.name}`]}
                  />
                ))}
              </div>
            </Card>
          )}

          <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={() => navigate("/setup/staff")}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? "Save Changes" : "Send Invitation"}
                </>
              )}
            </Button>
          </div>
        </form>
      </PageContainer>

      <ReAuthModal
        open={showReAuthModal}
        onOpenChange={setShowReAuthModal}
        onConfirm={handleReAuthConfirm}
        password={reAuthPassword}
        onPasswordChange={setReAuthPassword}
        loading={saving}
        title="Re-authentication Required"
        description="Please enter your password to save staff changes."
      />
    </AppLayout>
  );
};

export default StaffFormPage;
